import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import analytics from '../Services/analyticsService';

export const BOT_CONFIGS = {
  kafi: {
    key: 'kafi',
    name: 'Kafi',
    tier: 'easy',
    answerRate: 0.75,
    rewardOnWin: 0,
    rewardOnTie: 0,
    color: '#10b981',
    tagline: 'A friendly starter bot.',
    difficultyLabel: 'Easy',
  },
  kosi: {
    key: 'kosi',
    name: 'Kosi',
    tier: 'medium',
    answerRate: 0.95,
    rewardOnWin: 0,
    rewardOnTie: 0,
    color: '#f59e0b',
    tagline: 'Consistent and competitive.',
    difficultyLabel: 'Medium',
  },
  kemi: {
    key: 'kemi',
    name: 'Kemi',
    tier: 'hard',
    answerRate: 1.0,
    rewardOnWin: 30,
    rewardOnTie: 15,
    color: '#be185d',
    tagline: 'Beat her in Full Game for coins!',
    difficultyLabel: 'Hard',
  },
};

export const CATEGORIES = ['name', 'animal', 'place', 'thing'];

const INITIAL_GAME_STATE = {
  gamePhase: 'idle', // idle | playing | reviewing | round_results | finished
  selectedBot: null,
  gameMode: null, // quick | full
  enableSpecialRounds: false,
  currentRound: 0,
  totalRounds: 0,
  categories: CATEGORIES,
  currentLetter: null,
  playedLetters: [],
  userAnswers: {},
  botAnswers: {},
  userScore: 0,
  botScore: 0,
  roundHistory: [],
  specialRounds: [],
  submissionTime: null,
  gameStartedAt: null,
};

const useSoloStore = create(
  persist(
    (set, get) => ({
      ...INITIAL_GAME_STATE,

      stats: {
        kafi: { wins: 0, losses: 0, ties: 0 },
        kosi: { wins: 0, losses: 0, ties: 0 },
        kemi: { wins: 0, losses: 0, ties: 0 },
      },

      startGame: (botKey, gameMode, enableSpecialRounds, specialRounds = []) => {
        const totalRounds = gameMode === 'quick' ? 5 : 26;
        set({
          ...INITIAL_GAME_STATE,
          gamePhase: 'playing',
          selectedBot: botKey,
          gameMode,
          enableSpecialRounds,
          totalRounds,
          specialRounds: enableSpecialRounds ? specialRounds : [],
          currentRound: 1,
          gameStartedAt: Date.now(),
        });
        analytics.track('solo_game_started', { mode: gameMode, special_rounds_enabled: !!enableSpecialRounds });
      },

      setCurrentLetter: (letter) => set({ currentLetter: letter }),

      setBotAnswers: (answers) => set({ botAnswers: answers }),

      setUserAnswers: (answers, submissionTime) =>
        set({ userAnswers: answers, submissionTime, gamePhase: 'reviewing' }),

      submitRound: (userCategoryScores, botCategoryScores, specialEffect) => {
        const state = get();
        const userRoundTotal = Object.values(userCategoryScores).reduce(
          (sum, v) => sum + Math.max(0, v),
          0
        );
        const botRoundTotal = Object.values(botCategoryScores).reduce(
          (sum, v) => sum + Math.max(0, v),
          0
        );

        const roundEntry = {
          round: state.currentRound,
          letter: state.currentLetter,
          userAnswers: { ...state.userAnswers },
          botAnswers: { ...state.botAnswers },
          userScores: userCategoryScores,
          botScores: botCategoryScores,
          userTotal: userRoundTotal,
          botTotal: botRoundTotal,
          specialEffect: specialEffect || null,
        };

        set((s) => ({
          userScore: s.userScore + userRoundTotal,
          botScore: s.botScore + botRoundTotal,
          roundHistory: [...s.roundHistory, roundEntry],
          gamePhase: 'round_results',
        }));

        analytics.track('solo_round_completed', {
          round_number: state.currentRound,
          score: userRoundTotal,
          ...(specialEffect ? { special_round_type: specialEffect } : {}),
        });
      },

      // Patch the last round's user scores after a dispute re-review
      updateLastRoundScores: (updatedUserScores) => {
        set((state) => {
          const history = [...state.roundHistory];
          const last = history[history.length - 1];
          if (!last) return {};
          const merged = { ...last.userScores, ...updatedUserScores };
          const newTotal = Object.values(merged).reduce((sum, v) => sum + Math.max(0, v), 0);
          const diff = newTotal - last.userTotal;
          history[history.length - 1] = { ...last, userScores: merged, userTotal: newTotal };
          return { roundHistory: history, userScore: state.userScore + diff };
        });
      },

      nextRound: () => {
        const state = get();
        if (state.currentRound >= state.totalRounds) {
          set({ gamePhase: 'finished' });
        } else {
          set({
            currentRound: state.currentRound + 1,
            gamePhase: 'playing',
            userAnswers: {},
            botAnswers: {},
            currentLetter: null,
            submissionTime: null,
          });
        }
      },

      endGame: (result) => {
        const state = get();
        const bot = state.selectedBot;
        if (!bot) return;
        set((s) => ({
          stats: {
            ...s.stats,
            [bot]: {
              wins: s.stats[bot].wins + (result === 'win' ? 1 : 0),
              losses: s.stats[bot].losses + (result === 'loss' ? 1 : 0),
              ties: s.stats[bot].ties + (result === 'tie' ? 1 : 0),
            },
          },
        }));

        analytics.track('solo_game_completed', {
          did_win: result === 'win',
          total_score: state.userScore,
          mode: state.gameMode,
          duration_sec: state.gameStartedAt ? Math.round((Date.now() - state.gameStartedAt) / 1000) : 0,
        });
      },

      resetGame: () => set({ ...INITIAL_GAME_STATE }),
    }),
    {
      name: 'nameit-solo-store',
      partialize: (state) => ({
        stats: state.stats,
        // Persist full game state so refresh doesn't lose progress
        gamePhase: state.gamePhase,
        selectedBot: state.selectedBot,
        gameMode: state.gameMode,
        enableSpecialRounds: state.enableSpecialRounds,
        currentRound: state.currentRound,
        totalRounds: state.totalRounds,
        categories: state.categories,
        currentLetter: state.currentLetter,
        playedLetters: state.playedLetters,
        userScore: state.userScore,
        botScore: state.botScore,
        roundHistory: state.roundHistory,
        specialRounds: state.specialRounds,
        submissionTime: state.submissionTime,
        userAnswers: state.userAnswers,
        gameStartedAt: state.gameStartedAt,
        // botAnswers intentionally excluded — regenerated on mount
      }),
      version: 2, // bump to clear old persisted state
    }
  )
);

export default useSoloStore;
